// src/layouts/MainLayout.tsx
import React, { useState } from "react";
import { Link, Outlet, useNavigate } from "react-router-dom";
import {
  Menu,
  X,
  LogIn,
  UserPlus,
  Grid,
  LogOut,
  ShieldCheck,
} from "lucide-react";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import { useLogoutMutation } from "../app/api/authApi";
import { clearCredentials } from "../app/features/authSlice";

/**
 * MainLayout — public layout wrapping the Home page, event pages,
 * auth pages, and any other public routes.
 *
 * Nav tabs (public, unauthenticated):
 *   Home · About · Leadership · Gallery · Programme (event landing) · Contact
 *
 * Authenticated extra items:
 *   Dashboard (member) or Admin Panel (admin) + Logout
 */

/* ─────────────────────────────────────────────────────────────────
   NAV ITEMS
   Sections on the Home page use href= (scroll anchor).
   Standalone pages use to= (router Link).
───────────────────────────────────────────────────────────────── */

interface NavItem {
  label: string;
  href?: string; // anchor link — <a href>
  to?: string; // router link — <Link to>
}

const NAV_ITEMS: NavItem[] = [
  { label: "About", href: "/#about" },
  { label: "Leadership", href: "/#leaders" },
  { label: "Gallery", href: "/#gallery" },
  { label: "Programme", href: "/#pillars" },
  { label: "Event", to: "/event" },
];

/* ─────────────────────────────────────────────────────────────────
   COMPONENT
───────────────────────────────────────────────────────────────── */

const MainLayout: React.FC = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();
  // const location = useLocation();
  const dispatch = useAppDispatch();
  const [logoutApi] = useLogoutMutation();

  const { token, user } = useAppSelector((s) => s.auth);
  const authenticated = Boolean(token);
  const isAdmin = user?.role === "admin";

  async function handleLogout() {
    try {
      await logoutApi(undefined).unwrap();
    } catch {
      /* ignore */
    } finally {
      dispatch(clearCredentials());
      navigate("/");
    }
  }

  /* ── Shared class strings ── */
  const deskLink =
    "text-white/70 hover:text-white text-[11px] font-bold tracking-[0.14em] uppercase transition-colors duration-150";
  const mobLink =
    "block py-3 text-white/70 hover:text-white text-[11px] font-bold tracking-[0.14em] uppercase transition-colors border-b border-white/[0.06] last:border-0";

  const renderNavItem = (item: NavItem, mobile = false) => {
    const cls = mobile ? mobLink : deskLink;
    if (item.href) {
      return (
        <a
          key={item.label}
          href={item.href}
          className={cls}
          onClick={() => mobile && setMobileOpen(false)}
        >
          {item.label}
        </a>
      );
    }
    return (
      <Link
        key={item.label}
        to={item.to!}
        className={cls}
        onClick={() => mobile && setMobileOpen(false)}
      >
        {item.label}
      </Link>
    );
  };

  return (
    <div className="min-h-screen flex flex-col bg-white">
      {/* ── HEADER ──────────────────────────────────────────────────── */}
      {/* Always dark-green — consistent with EventLandingPage sticky nav */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-green-950/95 backdrop-blur-md border-b border-white/[0.06] h-16 flex items-center">
        <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between gap-6">
          {/* ── Logo ── */}
          <Link to="/" className="flex items-center gap-3 shrink-0">
            {/* Cross mark — simple inline SVG, no image dependency */}
            <div className="w-8 h-8 rounded bg-green-600 flex items-center justify-center shrink-0">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path
                  d="M8 2v12M2 8h12"
                  stroke="#fff"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                />
              </svg>
            </div>
            <span className="text-white font-black tracking-[0.14em] uppercase text-sm leading-none">
              GIMLP
            </span>
          </Link>

          {/* ── Desktop nav ── */}
          <nav className="hidden md:flex items-center gap-7 flex-1 justify-center">
            {NAV_ITEMS.map((item) => renderNavItem(item))}
          </nav>

          {/* ── Auth — desktop ── */}
          <div className="hidden md:flex items-center gap-2 shrink-0">
            {!authenticated ? (
              <>
                <Link
                  to="/login"
                  className="inline-flex items-center gap-1.5 px-4 py-2 text-white/70 hover:text-white text-[11px] font-bold tracking-[0.14em] uppercase transition-colors"
                >
                  <LogIn size={13} />
                  Sign In
                </Link>
                <Link
                  to="/register"
                  className="inline-flex items-center gap-1.5 px-5 py-2.5 bg-green-600 hover:bg-green-500 text-white text-[11px] font-black tracking-[0.14em] uppercase rounded transition-all active:scale-95 shadow shadow-green-900/40"
                >
                  <UserPlus size={13} />
                  Join
                </Link>
              </>
            ) : (
              <>
                {!isAdmin && (
                  <Link
                    to="/user/dashboard"
                    className="inline-flex items-center gap-1.5 px-4 py-2 text-white/70 hover:text-white text-[11px] font-bold tracking-[0.14em] uppercase transition-colors"
                  >
                    <Grid size={13} />
                    Dashboard
                  </Link>
                )}
                {isAdmin && (
                  <Link
                    to="/admin/dashboard"
                    className="inline-flex items-center gap-1.5 px-4 py-2 text-white/70 hover:text-white text-[11px] font-bold tracking-[0.14em] uppercase transition-colors"
                  >
                    <ShieldCheck size={13} />
                    Admin
                  </Link>
                )}
                <button
                  onClick={handleLogout}
                  className="inline-flex items-center gap-1.5 px-5 py-2.5 bg-green-600 hover:bg-green-500 text-white text-[11px] font-black tracking-[0.14em] uppercase rounded transition-all active:scale-95"
                >
                  <LogOut size={13} />
                  Sign Out
                </button>
              </>
            )}
          </div>

          {/* ── Hamburger ── */}
          <button
            onClick={() => setMobileOpen((v) => !v)}
            className="md:hidden p-2 text-white/70 hover:text-white transition-colors"
            aria-label="Toggle navigation"
          >
            {mobileOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>

        {/* ── Mobile menu ── */}
        {mobileOpen && (
          <div className="absolute top-full left-0 right-0 md:hidden bg-green-950 border-t border-white/10 shadow-2xl">
            <div className="px-6 pt-2 pb-6">
              {/* Nav links */}
              <div className="mb-5">
                {NAV_ITEMS.map((item) => renderNavItem(item, true))}
              </div>

              {/* Auth buttons */}
              <div className="flex flex-col gap-2.5">
                {!authenticated ? (
                  <>
                    <Link
                      to="/login"
                      className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 border border-white/20 hover:border-white/40 text-white text-[11px] font-bold tracking-[0.14em] uppercase rounded transition-colors"
                      onClick={() => setMobileOpen(false)}
                    >
                      <LogIn size={13} />
                      Sign In
                    </Link>
                    <Link
                      to="/register"
                      className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 bg-green-600 hover:bg-green-500 text-white text-[11px] font-black tracking-[0.14em] uppercase rounded transition-all"
                      onClick={() => setMobileOpen(false)}
                    >
                      <UserPlus size={13} />
                      Join the Programme
                    </Link>
                  </>
                ) : (
                  <>
                    {!isAdmin && (
                      <Link
                        to="/user/dashboard"
                        className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 border border-white/20 hover:border-white/40 text-white text-[11px] font-bold tracking-[0.14em] uppercase rounded transition-colors"
                        onClick={() => setMobileOpen(false)}
                      >
                        <Grid size={13} />
                        Dashboard
                      </Link>
                    )}
                    {isAdmin && (
                      <Link
                        to="/admin/dashboard"
                        className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 border border-white/20 hover:border-white/40 text-white text-[11px] font-bold tracking-[0.14em] uppercase rounded transition-colors"
                        onClick={() => setMobileOpen(false)}
                      >
                        <ShieldCheck size={13} />
                        Admin Panel
                      </Link>
                    )}
                    <button
                      onClick={() => {
                        handleLogout();
                        setMobileOpen(false);
                      }}
                      className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 bg-green-600 hover:bg-green-500 text-white text-[11px] font-black tracking-[0.14em] uppercase rounded transition-all"
                    >
                      <LogOut size={13} />
                      Sign Out
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </header>

      {/* ── PAGE CONTENT ── */}
      {/*
        The Home page hero fills to the very top of the viewport — the header
        is transparent-ish (bg-green-950/95) so it blends with the hero overlay.
        We therefore do NOT add pt-16 here; the Home hero section itself has
        enough padding to clear the header (py-24 → 96px > 64px header).
        Auth pages and other short pages should add their own top padding.
      */}
      <div className="flex-1">
        <Outlet />
      </div>

      {/* ── FOOTER ── */}
      <footer className="bg-green-950 px-4 md:px-10 py-7 flex flex-wrap items-center justify-between gap-4">
        <p className="text-white/35 text-sm">
          GIMLP © {new Date().getFullYear()}. RCCG Continent 2. All rights
          reserved.
        </p>
        <div className="flex gap-6">
          {[
            { label: "Privacy Policy", href: "/privacy-policy" },
            { label: "Data Protection", href: "/data-protection" },
          ].map((l) => (
            <a
              key={l.label}
              href={l.href}
              className="text-white/35 text-sm hover:text-white transition-colors no-underline"
            >
              {l.label}
            </a>
          ))}
        </div>
      </footer>
    </div>
  );
};

export default MainLayout;
